import React from 'react';
import { useLocation, Navigate, useOutlet } from 'react-router-dom';
import { useAuth } from '../Auth';
import NavigationBar from '../components/navigation/NavigationBar';
import Sidebar from '../components/navigation/Sidebar/Sidebar';
import ErrorBoundary from '../components/ErrorBoundary';
import { RouteChangeListener } from '../lib/RouteChangeListener';
import { analytics, AnalyticsEvents } from '../lib/analytics';
import styles from './PrivateLayout.module.scss';
// Token management is now handled by lib/auth-client.ts

interface PrivateLayoutProps {
  children?: React.ReactNode;
  title?: string;
}

const PrivateLayout: React.FC<PrivateLayoutProps> = ({ children }) => {
  const auth = useAuth();
  const outlet = useOutlet();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);

  if (auth.user == null) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If the user is authenticated but their email is not verified,
  // redirect them to the verify email page
  if (!auth.emailVerified) {
    const email = auth.user?.email || '';
    return <Navigate to={`/auth/verify-email?pending=true&email=${encodeURIComponent(email)}`} replace />;
  }

  // Set user identity for analytics when auth state changes
  React.useEffect(() => {
    if (auth.user?.id) {
      analytics.setUserId(String(auth.user.id));
    }
  }, [auth.user?.id]);

  return (
    <div className={styles.privateRoot}>
      <RouteChangeListener />
      <NavigationBar onOpenNav={() => setMobileNavOpen(true)} />

      <div className={styles.privateLayoutContainer}>
        <Sidebar
          mobileOpen={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
        />
        <div className={styles.contentArea}>
          <main className={styles.mainContent}>
            <div className={styles.contentWrapper}>
              <ErrorBoundary showSidebar>
                {children || outlet}
              </ErrorBoundary>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default PrivateLayout;
